import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useBatteries,
  useBattery,
  useCreateBattery,
  useDeleteBattery,
  useDevelopmentConfig,
  useSaveResults,
  useSetBatteryTests,
  useUpdateBattery,
} from '@/app/queries';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { formatDate, sheetName } from '@/lib/format';
import { configLabel } from '@/lib/labels';
import { resolveBatteryTests } from '@/lib/testing';
import { todayISO } from '@shared/dates';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { Matrix } from '@/ui/Matrix';
import { EmptyState, Notice, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { Sheet, SheetHead, SheetRow } from '@/ui/Sheet';

/** Percent change, drawn so the sign means better or worse rather than up or down. */
function Progress({
  progress,
}: {
  progress: { delta: number; percent: number; improved: boolean } | null;
}) {
  if (!progress || progress.percent === 0) return null;
  return (
    <span
      className={`tabular text-meta ${progress.improved ? 'text-ok' : 'text-risk'}`}
      dir="ltr"
    >
      {progress.improved ? '▲' : '▼'} {Math.abs(progress.percent).toFixed(1)}%
    </span>
  );
}

export function Testing({ locale, teamId }: { locale: Locale; teamId: string }) {
  const t = useTranslations();
  const config = useDevelopmentConfig();
  const list = useBatteries(teamId);
  const create = useCreateBattery();
  const update = useUpdateBattery();
  const remove = useDeleteBattery();
  const { busy, formError, fieldError, submit } = useAuthForm();

  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [onDate, setOnDate] = useState(todayISO());
  const [conditions, setConditions] = useState('');
  const [notes, setNotes] = useState('');

  const battery = useBattery(selectedId);
  const saveResults = useSaveResults(selectedId ?? '');
  const setTests = useSetBatteryTests(selectedId ?? '');

  // The grid's own state: keyed by player and test so a cell is addressable.
  const [values, setValues] = useState<Record<string, string>>({});
  const [chosenTests, setChosenTests] = useState<string[]>([]);
  const [saveReport, setSaveReport] = useState<{ saved: number; rejected: number } | null>(null);

  const rows = list.data ?? [];
  const tests = config.data?.tests ?? [];

  /*
    Seeded once per testing day, not once per fetch.

    Keyed on `battery.data` alone, this ran again on every refetch — and saving
    the chosen tests invalidates exactly that query, so a coach who toggled a
    test mid-entry had every number he had typed and not yet saved replaced by
    what was last on the server.
  */
  const seeded = useRef<string | null>(null);
  useEffect(() => {
    const data = battery.data;
    if (!data || seeded.current === data.battery.id) return;
    seeded.current = data.battery.id;

    const next: Record<string, string> = {};
    for (const r of data.results) {
      next[`${r.playerId}:${r.testKey}`] = r.value === null ? '' : String(r.value);
    }
    setValues(next);
    setChosenTests(
      resolveBatteryTests(data.battery.testKeys, data.tests, data.results).map((x) => x.key),
    );
    setSaveReport(null);
  }, [battery.data]);

  const startCreate = () => {
    setEditingId(null);
    setName('');
    setOnDate(todayISO());
    setConditions('');
    setNotes('');
    setOpenForm(true);
  };

  const startEdit = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    setEditingId(id);
    setName(row.name);
    setOnDate(row.onDate);
    setConditions(row.conditions ?? '');
    setNotes(row.notes ?? '');
    setOpenForm(true);
  };

  const saveBattery = async () => {
    const input = {
      name,
      onDate,
      conditions: conditions || null,
      notes: notes || null,
    };
    const ok = await submit(async () => {
      if (editingId) {
        await update.mutateAsync({ id: editingId, input });
      } else {
        const created = await create.mutateAsync({ teamId, ...input });
        setSelectedId(created.id);
      }
    });
    if (ok) setOpenForm(false);
  };

  const shown = useMemo(
    () => (battery.data?.tests ?? tests).filter((x) => chosenTests.includes(x.key)),
    [battery.data?.tests, tests, chosenTests],
  );

  /*
    The choice is part of the day, so it is saved the moment it is made.

    It used to live only here, which is why the blank sheet printed every test
    the club has ever defined: nothing downstream could know that this Thursday
    was five physical tests and not twenty.
  */
  const toggleTest = (key: string) => {
    const next = chosenTests.includes(key)
      ? chosenTests.filter((k) => k !== key)
      : [...chosenTests, key];
    setChosenTests(next);
    if (selectedId) setTests.mutate(next);
  };

  const previousOf = useMemo(() => {
    const map = new Map<string, { previous: number | null; progress: { delta: number; percent: number; improved: boolean } | null }>();
    for (const r of battery.data?.results ?? []) {
      map.set(`${r.playerId}:${r.testKey}`, { previous: r.previous, progress: r.progress });
    }
    return map;
  }, [battery.data?.results]);

  const commitResults = async () => {
    if (!selectedId) return;
    const payload = Object.entries(values).map(([key, raw]) => {
      const [playerId, testKey] = key.split(':');
      const parsed = raw.trim() === '' ? null : Number(raw.replace(',', '.'));
      return {
        playerId: playerId!,
        testKey: testKey!,
        value: parsed !== null && Number.isFinite(parsed) ? parsed : null,
      };
    });
    const result = await saveResults.mutateAsync(payload);
    setSaveReport({ saved: result.saved, rejected: result.rejected.length });
  };

  return (
    <>
      {openForm ? (
        <div className="mb-5">
          <Panel
            title={editingId ? t('testing.edit') : t('testing.add')}
            action={
              <Button variant="quiet" onClick={() => setOpenForm(false)}>
                {t('common.cancel')}
              </Button>
            }
          >
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void saveBattery();
              }}
            >
              {formError ? (
                <div className="px-4 pt-4">
                  <Notice tone="risk">{formError}</Notice>
                </div>
              ) : null}
              <div className="grid gap-x-5 gap-y-2 p-4 sm:grid-cols-2">
                <Field label={t('testing.name')} error={fieldError('name')}>
                  {(p) => (
                    <TextInput {...p} value={name} onChange={(e) => setName(e.target.value)} required />
                  )}
                </Field>
                <Field label={t('testing.date')} error={fieldError('onDate')}>
                  {(p) => (
                    <TextInput
                      {...p}
                      type="date"
                      value={onDate}
                      onChange={(e) => setOnDate(e.target.value)}
                      required
                    />
                  )}
                </Field>
                <Field label={t('testing.conditions')} optional hint={t('testing.conditionsHint')}>
                  {(p) => (
                    <TextInput
                      {...p}
                      value={conditions}
                      onChange={(e) => setConditions(e.target.value)}
                    />
                  )}
                </Field>
                <Field label={t('testing.notes')} optional>
                  {(p) => (
                    <TextArea {...p} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                  )}
                </Field>
              </div>
              <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-4">
                <Button type="submit" variant="primary" busy={busy} disabled={!name}>
                  {t('testing.save')}
                </Button>
                {editingId ? (
                  <Button
                    variant="danger"
                    onClick={() => {
                      if (!window.confirm(t('testing.confirmDelete'))) return;
                      void remove.mutateAsync(editingId).then(() => {
                        setOpenForm(false);
                        if (selectedId === editingId) setSelectedId(null);
                      });
                    }}
                  >
                    {t('testing.delete')}
                  </Button>
                ) : null}
              </div>
            </form>
          </Panel>
        </div>
      ) : null}

      <div className="grid gap-5">
        <Panel
          title={t('testing.title')}
          explain="test_direction"
          count={rows.length}
          action={
            openForm ? null : (
              <Button variant="primary" onClick={startCreate}>
                {t('testing.add')}
              </Button>
            )
          }
        >
          {list.isLoading ? (
            <p className="flex items-center gap-2 px-4 py-6 text-ink-muted">
              <Spinner /> {t('common.loading')}
            </p>
          ) : rows.length === 0 ? (
            <EmptyState
              title={t('testing.empty')}
              body={t('testing.emptyBody')}
              action={
                <Button variant="primary" onClick={startCreate}>
                  {t('testing.add')}
                </Button>
              }
            />
          ) : (
            <>
              <SheetHead>
                <span className="w-24 shrink-0">{t('testing.date')}</span>
                <span className="flex-1">{t('testing.name')}</span>
                <span className="w-24 shrink-0 text-end">{t('testing.results')}</span>
              </SheetHead>
              <Sheet>
                {rows.map((r) => (
                  <SheetRow
                    key={r.id}
                    highlight={r.id === selectedId}
                    onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}
                  >
                    <span className="tabular w-24 shrink-0 text-meta text-ink-muted">
                      {formatDate(r.onDate, locale)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{r.name}</span>
                      {r.conditions ? (
                        <span className="block truncate text-meta text-ink-faint">{r.conditions}</span>
                      ) : null}
                    </span>
                    <span className="tabular w-24 shrink-0 text-end text-meta text-ink-muted">
                      {t('testing.playersTested', { count: r.playersTested })}
                    </span>
                  </SheetRow>
                ))}
              </Sheet>
            </>
          )}
        </Panel>

        {selectedId && battery.data ? (
          <Panel
            title={battery.data.battery.name}
            count={formatDate(battery.data.battery.onDate, locale)}
            action={
              <div className="flex gap-2">
                <Button variant="quiet" onClick={() => startEdit(selectedId)}>
                  {t('testing.edit')}
                </Button>
                <Button
                  variant="primary"
                  busy={saveResults.isPending}
                  onClick={() => void commitResults()}
                >
                  {t('testing.saveResults')}
                </Button>
              </div>
            }
          >
            <Section title={t('testing.pickTest')}>
              <div className="flex flex-wrap gap-1.5">
                {battery.data.tests.map((x) => {
                  const on = chosenTests.includes(x.key);
                  return (
                    <button
                      key={x.key}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleTest(x.key)}
                      className={`min-h-11 rounded-(--radius-control) border px-3 text-meta transition-colors ${
                        on
                          ? 'border-ink bg-ink text-paper'
                          : 'border-line bg-surface text-ink-muted hover:border-ink hover:text-ink'
                      }`}
                    >
                      {configLabel(x, t)}
                    </button>
                  );
                })}
              </div>
              {/* Where the list itself lives. A coach looking at these chips
                  and not finding the test he ran is one tap from fixing it,
                  rather than asking somebody to ship a release. */}
              <p className="mt-3 text-meta text-ink-faint">
                <Link
                  to={localePath(locale, '/settings')}
                  className="underline decoration-line-strong underline-offset-2 hover:decoration-ink"
                >
                  {t('testCatalogue.manage')}
                </Link>
              </p>
            </Section>

            {saveReport ? (
              <div className="px-4">
                <Notice tone={saveReport.rejected > 0 ? 'risk' : 'ok'}>
                  {saveReport.rejected > 0
                    ? t('testing.outOfRange', { count: saveReport.rejected })
                    : t('testing.saved', { count: saveReport.saved })}
                </Notice>
              </div>
            ) : null}

            {shown.length === 0 ? (
              <p className="px-4 py-6 text-meta text-ink-muted">{t('testing.noTests')}</p>
            ) : (
              /*
                A card per player below `md`. The column count grows with
                however many tests a club defines, so no amount of narrowing
                fixes this one — a club with eight tests simply has a wider
                table than any phone. Transposing is the only shape that holds.
              */
              <Matrix
                rows={battery.data.squad}
                rowKey={(p: (typeof battery.data.squad)[number]) => p.playerId}
                caption={t('testing.title')}
                columns={[
                  {
                    key: 'player',
                    label: t('evaluation.player'),
                    heading: true,
                    cell: (p: (typeof battery.data.squad)[number]) => (
                      <Link
                        to={localePath(locale, `/development/players/${p.playerId}`)}
                        className="inline-flex min-h-11 items-center hover:underline"
                      >
                        <span className="tabular me-2 text-ink-faint">{p.shirtNumber ?? '–'}</span>
                        {sheetName(p.knownAs ?? p.firstName, p.lastName)}
                      </Link>
                    ),
                  },
                  ...shown.map((x) => ({
                    key: x.key,
                    label: (
                      <>
                        <span className="block text-ink-muted">{configLabel(x, t)}</span>
                        <span className="block">
                          {x.unit ? t('testing.unitLabel', { unit: x.unit }) : ''}
                          {' · '}
                          {x.direction === 'lower'
                            ? t('testing.lowerIsBetter')
                            : t('testing.higherIsBetter')}
                        </span>
                      </>
                    ),
                    cell: (p: (typeof battery.data.squad)[number]) => {
                      const key = `${p.playerId}:${x.key}`;
                      const history = previousOf.get(key);
                      return (
                        <>
                          <TextInput
                            type="number"
                            step="0.01"
                            inputMode="decimal"
                            aria-label={`${sheetName(p.firstName, p.lastName)} — ${configLabel(x, t)}`}
                            className="w-20"
                            value={values[key] ?? ''}
                            onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                          />
                          {history?.previous !== null && history?.previous !== undefined ? (
                            <span className="mt-1 flex items-center gap-1.5">
                              <span className="tabular text-meta text-ink-faint">
                                {history.previous}
                              </span>
                              <Progress progress={history.progress} />
                            </span>
                          ) : null}
                        </>
                      );
                    },
                  })),
                ]}
              />
            )}
          </Panel>
        ) : null}
      </div>
    </>
  );
}
